<script lang="ts">
  import type { Snippet } from "svelte";
  import ArrowLeft from "../icons/ArrowLeft.svelte";
  import Skeleton from "./Skeleton.svelte";
  import { navigate } from "svelte5-router";

  interface Props {
    title: string;
    titleExtra?: Snippet;
    description?: string;
    breadcrumbText?: string;
    breadcrumbPath?: string;
    breadcrumbLoading?: boolean;
    fixedHeader?: boolean;
    children?: Snippet;
    actions?: Snippet;
  }

  const {
    children,
    description,
    title,
    titleExtra,
    breadcrumbText,
    breadcrumbPath = "/",
    breadcrumbLoading = false,
    fixedHeader = false,
    actions,
  }: Props = $props();

  function handleBack() {
    navigate(breadcrumbPath);
  }
</script>

<svelte:head>
  <title>{title} | Poolside</title>
</svelte:head>

<div
  class={{
    "flex h-full flex-col bg-mono-000": fixedHeader,
    "bg-mono-000 px-4 pb-20 sm:px-10": !fixedHeader,
    "pt-5": !fixedHeader && breadcrumbText,
    "pt-4": !fixedHeader && !breadcrumbText,
  }}
>
  <div
    class={{
      "sticky top-0 z-20 bg-mono-000 px-4 py-4 sm:px-10": fixedHeader,
    }}
  >
    {#if breadcrumbText}
      <div class="mb-6 h-5">
        {#if breadcrumbLoading}
          <div class="flex h-5 items-center gap-1.5 text-sm text-foreground-tertiary">
            <ArrowLeft class="transition-transform group-hover:translate-x-[-4px]" size={16} />
            <Skeleton class="rounded" height={14} width={100} />
          </div>
        {:else}
          <a
            class="flex h-5 items-center gap-1.5 text-sm text-foreground-tertiary no-underline transition-colors ease-out hover:cursor-pointer hover:text-foreground-primary"
            href={breadcrumbPath}
            onclick={(e) => {
              if (!e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                handleBack();
              }
            }}
          >
            <ArrowLeft class="transition-transform group-hover:translate-x-[-4px]" size={16} />
            {breadcrumbText}
          </a>
        {/if}
      </div>
    {/if}
    <div
      class={{
        "mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between": !fixedHeader,
      }}
    >
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center gap-3.5">
          <h1 class="text-2xl tracking-tight text-mono-900 sm:text-3xl">
            {title}
          </h1>
          {#if titleExtra}
            <div class="flex items-center gap-2">
              {@render titleExtra()}
            </div>
          {/if}
        </div>
        {#if description}
          <p class="text-sm text-mono-800">{description}</p>
        {/if}
      </div>
      {#if actions}
        <div class="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          {@render actions()}
        </div>
      {/if}
    </div>
  </div>
  {#if fixedHeader}
    <div class="flex-1 overflow-y-auto px-4 pb-4 sm:px-10">
      {@render children?.()}
    </div>
  {:else}
    {@render children?.()}
  {/if}
</div>
